import { openConfirmPrompt } from "@/components/providers/AlertDialogProvider";
import { toaster } from "@/components/providers/ToasterProvider";
import router from "@/router/index";
import { $t } from "@/services/i18n";
import { ALL_PROJECTS_ID } from "@/stores/project.store";

/**
 * Requests a project switch with UX improvements:
 * - Always shows a toast when project is switched successfully
 * - On protected routes (projectSwitchProtection: true), shows a confirmation dialog first
 *
 * @param targetProjectId - The ID of the project to switch to
 * @param targetProjectName - The display name of the project
 * @returns Promise<boolean> - true if switch should proceed, false if cancelled
 */
export async function requestProjectSwitch(
  targetProjectId: string,
  targetProjectName: string
): Promise<boolean> {
  const route = router.currentRoute.value;
  const isProtectedRoute = route.meta.projectSwitchProtection === true;

  if (!isProtectedRoute) {
    // Not protected: just show toast and proceed
    showProjectSwitchToast(targetProjectId, targetProjectName);
    return true;
  }

  // Protected route: show confirmation dialog
  return new Promise((resolve) => {
    openConfirmPrompt({
      title: $t("projects.switch_confirm_title"),
      description: $t("projects.switch_confirm_description"),
      confirmLabel: $t("projects.switch_confirm_button"),
      cancelLabel: $t("projects.switch_stay_button"),
      onConfirm: async (close) => {
        close();

        // Navigate to parent route
        const parentRouteName = route.meta.parent;
        if (parentRouteName && typeof parentRouteName === "string") {
          await router.push({ name: parentRouteName });
        } else {
          // Fallback to dashboard if no parent
          await router.push({ name: "Dashboard" });
        }

        // Show toast after navigation
        showProjectSwitchToast(targetProjectId, targetProjectName);
        resolve(true);
      },
      onCancel: () => {
        resolve(false);
      },
    });
  });
}

/**
 * Shows the appropriate toast message for a project switch
 */
function showProjectSwitchToast(projectId: string, projectName: string): void {
  if (projectId === ALL_PROJECTS_ID) {
    toaster.switch($t("projects.switched_to_all_projects"));
  } else {
    toaster.switch($t("projects.switched_to_project", { name: projectName }));
  }
}
